import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Users,
  FileText,
  TrendingUp,
  Radio,
  Phone,
  ClipboardList,
  Car,
  Download,
  ArrowUpDown,
  MapPin,
  CalendarDays,
  RefreshCw
} from "lucide-react";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const generatedBy = user?.name || user?.email || "Unknown User";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showArrestsDialog, setShowArrestsDialog] = useState(false);
  const [arrestRecords, setArrestRecords] = useState([]);
  const [loadingArrests, setLoadingArrests] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [avgPreset, setAvgPreset] = useState("all"); // "all", "ytd", "6m", "1m", "1w"
  // Per-unit drill-down dialog. avgUnitDialog: null = closed, "" = open
  // (no unit picked yet), otherwise the selected unit name.
  const [avgUnitDialog, setAvgUnitDialog] = useState(null);
  const [avgUnitSearch, setAvgUnitSearch] = useState("");

  // Unit detail dialog
  const [showUnitDialog, setShowUnitDialog] = useState(false);
  const [unitDetail, setUnitDetail] = useState(null);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [unitTab, setUnitTab] = useState("incidents");

  // Incidents by type sorting
  const [typeSort, setTypeSort] = useState("count_desc"); // count_desc, count_asc, name_asc, name_desc

  // Unit overview sorting
  const [unitSort, setUnitSort] = useState({ key: "unit", dir: "asc" });

  // Location incidents dialog
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationDialogTitle, setLocationDialogTitle] = useState("");
  const [locationIncidents, setLocationIncidents] = useState([]);
  const [confirmNavIncident, setConfirmNavIncident] = useState(null);

  // Date range filter
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Stats cache freshness: when the backend computed the stats it served, and
  // whether a manual Refresh (force recompute) is in flight.
  const [computedAt, setComputedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (fromDate, toDate, force = false) => {
    if (force) setRefreshing(true);
    try {
      const params = {};
      const df = fromDate !== undefined ? fromDate : dateFrom;
      const dt = toDate !== undefined ? toDate : dateTo;
      if (df) params.date_from = new Date(df).toISOString();
      if (dt) {
        // Set to end of day
        const end = new Date(dt);
        end.setHours(23, 59, 59, 999);
        params.date_to = end.toISOString();
      }
      if (force) params.force = true;
      const statsRes = await axios.get(`${API}/dashboard/stats`, { params, withCredentials: true });
      setStats(statsRes.data);
      setComputedAt(statsRes.data?.computed_at || null);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchArrestRecords = async () => {
    setLoadingArrests(true);
    try {
      const res = await axios.get(`${API}/dashboard/arrests`, { withCredentials: true });
      setArrestRecords(res.data.arrests || []);
    } catch (error) {
      console.error("Failed to fetch arrest records:", error);
    } finally {
      setLoadingArrests(false);
    }
  };

  const handleArrestsClick = () => {
    setShowArrestsDialog(true);
    fetchArrestRecords();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "M/d/yy h:mm a");
    } catch {
      return dateStr;
    }
  };

  const handleUnitSort = (key) => {
    setUnitSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc"
    }));
  };

  const getSortedUnitSummary = () => {
    if (!stats?.unit_summary) return [];
    const sorted = [...stats.unit_summary];
    sorted.sort((a, b) => {
      const aVal = a[unitSort.key];
      const bVal = b[unitSort.key];
      if (unitSort.key === "unit") {
        return unitSort.dir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return unitSort.dir === "desc" ? bVal - aVal : aVal - bVal;
    });
    return sorted;
  };

  const fetchUnitDetail = async (unitName, tab) => {
    setUnitTab(tab || "incidents");
    setShowUnitDialog(true);
    setLoadingUnit(true);
    try {
      const res = await axios.get(`${API}/dashboard/unit/${encodeURIComponent(unitName)}`, { withCredentials: true });
      setUnitDetail(res.data);
    } catch (error) {
      console.error("Failed to fetch unit detail:", error);
    } finally {
      setLoadingUnit(false);
    }
  };

  const formatUnitDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "MM/dd/yyyy");
    } catch {
      return dateStr.slice(0, 10);
    }
  };

  const formatUnitDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "MM/dd/yyyy h:mm a");
    } catch {
      return dateStr;
    }
  };

  // Date range label for exports
  const getDateRangeLabel = () => {
    if (!dateFrom && !dateTo) return "";
    const from = dateFrom ? format(new Date(dateFrom + "T00:00:00"), "MM/dd/yyyy") : "Start";
    const to = dateTo ? format(new Date(dateTo + "T00:00:00"), "MM/dd/yyyy") : "Present";
    return `Date Range: ${from} - ${to}`;
  };

  // "Updated X ago" for the cached stats. Recomputes against currentTime (the
  // 1s clock) so it ticks live without extra state.
  const getUpdatedAgoLabel = () => {
    if (!computedAt) return "";
    const secs = Math.max(0, Math.floor((currentTime - new Date(computedAt)) / 1000));
    if (secs < 10) return "Updated just now";
    if (secs < 60) return `Updated ${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Updated ${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `Updated ${hrs}h ago`;
  };

  // Generic export helpers
  const downloadCSV = (filename, headers, rows) => {
    const rangeLabel = getDateRangeLabel();
    const lines = [];
    lines.push(`"CHSP IMS - Generated by: ${generatedBy} | ${format(new Date(), "MM/dd/yyyy h:mm a")}"`);
    if (rangeLabel) lines.push(`"${rangeLabel}"`);
    lines.push("");
    lines.push(headers.join(","));
    lines.push(...rows.map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")));
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addPdfWatermarks = (doc) => {
    const totalPages = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const watermarkText = `CHSP - ${generatedBy}`;

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      doc.setPage(pageNum);

      // Diagonal background watermarks - 6 positions at 12% opacity
      doc.setFontSize(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(200, 200, 200);
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.12 }));
      const positions = [
        { x: pageWidth * 0.25, y: pageHeight * 0.25 },
        { x: pageWidth * 0.75, y: pageHeight * 0.25 },
        { x: pageWidth * 0.25, y: pageHeight * 0.5 },
        { x: pageWidth * 0.75, y: pageHeight * 0.5 },
        { x: pageWidth * 0.25, y: pageHeight * 0.75 },
        { x: pageWidth * 0.75, y: pageHeight * 0.75 },
      ];
      positions.forEach(pos => {
        doc.text(watermarkText, pos.x, pos.y, { align: 'center', angle: 45 });
      });
      doc.restoreGraphicsState();

      // Header bar
      doc.setFillColor(30, 58, 138);
      doc.rect(0, 0, pageWidth, 8, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(`CHSP INCIDENT MANAGEMENT SYSTEM | ${generatedBy} | Page ${pageNum}/${totalPages}`, pageWidth / 2, 5.5, { align: 'center' });

      // Footer
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text(`CHSP IMS - Generated by: ${generatedBy}`, 14, pageHeight - 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`${format(new Date(), "MM/dd/yyyy h:mm a")} | User: ${generatedBy}`, 14, pageHeight - 10);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 14, pageHeight - 10, { align: 'right' });
    }
  };

  const downloadPDF = async (title, headers, rows, filename) => {
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${format(new Date(), "MM/dd/yyyy h:mm a")}  |  By: ${generatedBy}`, 14, 28);
    const rangeLabel = getDateRangeLabel();
    let startY = 34;
    if (rangeLabel) {
      doc.setTextColor(200, 120, 0);
      doc.text(rangeLabel, 14, startY);
      doc.setTextColor(0, 0, 0);
      startY += 8;
    }
    doc.autoTable({
      startY,
      head: [headers],
      body: rows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 41, 59] },
      margin: { top: 12, bottom: 22 },
    });
    addPdfWatermarks(doc);
    doc.save(`${filename}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  // --- Card export functions ---

  // Incidents by Type
  const exportIncidentsByType = (type) => {
    const data = [...(stats?.incidents_by_type || [])].sort((a, b) => {
      if (typeSort === "count_desc") return b.count - a.count;
      if (typeSort === "count_asc") return a.count - b.count;
      if (typeSort === "name_asc") return a.type.localeCompare(b.type);
      return b.type.localeCompare(a.type);
    });
    if (!data.length) return;
    const h = ["Type", "Count"];
    const r = data.map(d => [d.type, d.count]);
    type === "csv" ? downloadCSV("incidents_by_type", h, r) : downloadPDF("Incidents by Type", h, r, "incidents_by_type");
  };

  // Unit Activity
  const exportUnitActivity = (type) => {
    const data = stats?.units_responded;
    if (!data?.length) return;
    const h = ["Unit", "Responses"];
    const r = data.map(d => [d.unit, d.count]);
    type === "csv" ? downloadCSV("unit_activity", h, r) : downloadPDF("Unit Activity (All Time)", h, r, "unit_activity");
  };

  // Dispatchers Activity
  const exportDispatchers = (type) => {
    const data = stats?.dispatchers;
    if (!data?.length) return;
    const h = ["Dispatcher", "Count"];
    const r = data.map(d => [d.dispatcher, d.count]);
    type === "csv" ? downloadCSV("dispatchers", h, r) : downloadPDF("Dispatchers Activity", h, r, "dispatchers");
  };

  // Top 5 Active Units
  const exportTop5 = (type) => {
    const data = stats?.top_5_active_units;
    if (!data?.length) return;
    const h = ["Unit", "Responses"];
    const r = data.map(d => [d.unit, d.count]);
    type === "csv" ? downloadCSV("top5_active", h, r) : downloadPDF(`Top 5 Active Units (${stats.top_5_period_start} - ${stats.top_5_period_end})`, h, r, "top5_active");
  };

  // Units Investigating
  const exportInvestigating = (type) => {
    const data = stats?.units_investigating;
    if (!data?.length) return;
    const h = ["Unit", "Investigations"];
    const r = data.map(d => [d.unit, d.count]);
    type === "csv" ? downloadCSV("units_investigating", h, r) : downloadPDF("Units Investigating", h, r, "units_investigating");
  };

  // Roll Call / Incident Mismatch
  const exportMismatch = (type) => {
    const rows = [];
    (stats?.rollcall_only_units || []).forEach(u => rows.push([u.unit, "Roll Call Only", u.rollcalls, 0]));
    (stats?.incidents_only_units || []).forEach(u => rows.push([u.unit, "Incidents Only", 0, u.incidents]));
    if (!rows.length) return;
    const h = ["Unit", "Status", "Roll Calls", "Incidents"];
    type === "csv" ? downloadCSV("mismatch", h, rows) : downloadPDF("Roll Call / Incident Mismatch", h, rows, "mismatch");
  };

  // Inactive Units
  const exportInactive = (type) => {
    if (!stats?.unit_summary?.length) return;
    const inactive = stats.unit_summary.filter(u => u.incidents === 0 && u.rollcalls === 0);
    if (!inactive.length) return;
    const h = ["Unit"];
    const r = inactive.map(u => [u.unit]);
    type === "csv" ? downloadCSV("inactive_units", h, r) : downloadPDF("Inactive Units", h, r, "inactive_units");
  };

  // More Roll Calls Than Incidents
  const exportMoreRollcall = (type) => {
    if (!stats?.unit_summary?.length) return;
    const data = stats.unit_summary.filter(u => u.rollcalls > u.incidents);
    if (!data.length) return;
    const h = ["Unit", "Roll Calls", "Incidents"];
    const r = data.map(u => [u.unit, u.rollcalls, u.incidents]);
    type === "csv" ? downloadCSV("more_rollcalls", h, r) : downloadPDF("More Roll Calls Than Incidents", h, r, "more_rollcalls");
  };

  // Response Analytics
  const exportResponseAnalytics = (type) => {
    const ra = stats?.response_analytics;
    if (!ra) return;
    const h = ["Metric", "Average", "Sample Size"];
    const r = [
      ["Time to First Unit Assigned", ra.avg_to_first_assigned, ra.sample_assigned],
      ["Time to First Unit 84 (On Scene)", ra.avg_to_first_84, ra.sample_84],
      ["Time to First Unit 98", ra.avg_to_first_98, ra.sample_98],
      ["Time to Call 98 (All Units Done)", ra.avg_to_call_98, ra.sample_call_98],
      ["Avg Units Per Call", ra.avg_units_per_call, ra.sample_units],
      ["Hotline Calls (All)", `${ra.hotline_count} (${ra.hotline_pct}%)`, ra.total_incidents],
      ["Hotline Dispatch", `${ra.hotline_live_count} (${ra.hotline_live_pct}%)`, ra.total_incidents],
      ["Avg Hotline to Dispatch", ra.avg_hotline_to_dispatch, ra.sample_hotline_dispatch],
    ];
    type === "csv" ? downloadCSV("response_analytics", h, r) : downloadPDF("Response Analytics", h, r, "response_analytics");
  };

  // Avg Responses Per Unit by Period
  const exportAvgByPeriod = (type) => {
    const cc = stats?.call_counter;
    if (!cc) return;
    const h = ["Period", "Total Responses", "Active Units", "Average"];
    const r = Object.entries(cc).filter(([k]) => k !== "all_time").map(([k, v]) => {
      const label = k === "today" ? "Today" : k === "week" ? "This Week" : k === "month" ? "This Month" : "This Year";
      return [label, v.total_responses || 0, v.active_units || 0, v.avg_per_unit?.toFixed(1) || "0"];
    });
    type === "csv" ? downloadCSV("avg_by_period", h, r) : downloadPDF("Avg Responses Per Unit by Period", h, r, "avg_by_period");
  };

  // Incident Locations
  const exportLocations = (type) => {
    const data = stats?.location_hotspots;
    if (!data?.length) return;
    const h = ["Location", "Count", "Types"];
    const r = data.map(d => [d.location, d.count, (d.types || []).join(", ")]);
    type === "csv" ? downloadCSV("location_hotspots", h, r) : downloadPDF("Incident Locations", h, r, "location_hotspots");
  };

  // Repeat Locations
  const exportRepeatLocations = (type) => {
    const data = stats?.repeat_locations;
    if (!data?.length) return;
    const h = ["Location", "Cross Streets", "Count", "Types"];
    const r = data.map(d => [d.location, d.cross_streets, d.count, (d.types || []).join(", ")]);
    type === "csv" ? downloadCSV("repeat_locations", h, r) : downloadPDF("Repeat Locations", h, r, "repeat_locations");
  };

  // Repeat Intersections
  const exportIntersections = (type) => {
    const data = stats?.intersection_hotspots;
    if (!data?.length) return;
    const h = ["Intersection", "Count", "Types"];
    const r = data.map(d => [d.intersection, d.count, (d.types || []).join(", ")]);
    type === "csv" ? downloadCSV("repeat_intersections", h, r) : downloadPDF("Repeat Intersections", h, r, "repeat_intersections");
  };

  // Patrol Sessions by Unit
  const exportPatrolSessions = (type) => {
    if (!stats?.unit_summary?.length) return;
    const data = stats.unit_summary.filter(u => u.patrol_sessions > 0).sort((a, b) => b.patrol_sessions - a.patrol_sessions);
    if (!data.length) return;
    const h = ["Unit", "Sessions"];
    const r = data.map(u => [u.unit, u.patrol_sessions]);
    type === "csv" ? downloadCSV("patrol_sessions", h, r) : downloadPDF("Patrol Sessions by Unit", h, r, "patrol_sessions");
  };

  // Patrol Hours by Unit
  const exportPatrolHours = (type) => {
    if (!stats?.unit_summary?.length) return;
    const data = stats.unit_summary.filter(u => u.patrol_hours > 0).sort((a, b) => b.patrol_hours - a.patrol_hours);
    if (!data.length) return;
    const h = ["Unit", "Hours"];
    const r = data.map(u => [u.unit, u.patrol_hours]);
    type === "csv" ? downloadCSV("patrol_hours", h, r) : downloadPDF("Patrol Hours by Unit", h, r, "patrol_hours");
  };

  // Roll Call Attendance
  const exportRollCall = (type) => {
    const data = stats?.rollcall_units;
    if (!data?.length) return;
    const h = ["Unit", "Attendances"];
    const r = data.map(d => [d.unit, d.count]);
    type === "csv" ? downloadCSV("rollcall_attendance", h, r) : downloadPDF("Roll Call Attendance", h, r, "rollcall_attendance");
  };

  const exportAvgCSV = () => {
    if (!stats?.unit_summary?.length) return;
    const unitsWithCalls = stats.unit_summary.filter(u => u.incidents > 0);
    if (unitsWithCalls.length === 0) return;
    const avg = unitsWithCalls.reduce((s, u) => s + u.incidents, 0) / unitsWithCalls.length;
    const all = [...stats.unit_summary].sort((a, b) => b.incidents - a.incidents);
    const headers = ["Unit", "Incidents", "Status", "Difference"];
    const rangeLabel = getDateRangeLabel();
    const lines = [];
    lines.push(`"CHSP IMS - Generated by: ${generatedBy} | ${format(new Date(), "MM/dd/yyyy h:mm a")}"`);
    if (rangeLabel) lines.push(`"${rangeLabel}"`);
    lines.push("");
    lines.push(headers.join(","));
    all.forEach(u => {
      const diff = u.incidents - avg;
      const status = u.incidents === 0 ? "No Calls" : u.incidents >= avg ? "Above" : "Below";
      lines.push([u.unit, u.incidents, status, diff >= 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1)].join(","));
    });
    lines.push(`\nAverage,${avg.toFixed(1)},${unitsWithCalls.length} active units,${unitsWithCalls.reduce((s, u) => s + u.incidents, 0)} total calls`);
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `unit_call_average_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAvgPDF = async () => {
    if (!stats?.unit_summary?.length) return;
    const unitsWithCalls = stats.unit_summary.filter(u => u.incidents > 0);
    if (unitsWithCalls.length === 0) return;
    const avg = unitsWithCalls.reduce((s, u) => s + u.incidents, 0) / unitsWithCalls.length;
    const below = stats.unit_summary.filter(u => u.incidents < avg).sort((a, b) => a.incidents - b.incidents);
    const above = stats.unit_summary.filter(u => u.incidents >= avg && u.incidents > 0).sort((a, b) => b.incidents - a.incidents);
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Unit Call Average Report", 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${format(new Date(), "MM/dd/yyyy h:mm a")}  |  By: ${generatedBy}`, 14, 28);
    let y = 35;
    const rangeLabel = getDateRangeLabel();
    if (rangeLabel) {
      doc.setTextColor(200, 120, 0);
      doc.text(rangeLabel, 14, y);
      doc.setTextColor(0, 0, 0);
      y += 7;
    }
    doc.text(`Average: ${avg.toFixed(1)} calls  |  ${unitsWithCalls.reduce((s, u) => s + u.incidents, 0)} total calls ÷ ${unitsWithCalls.length} active units`, 14, y);
    y += 7;
    if (above.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(34, 197, 94);
      doc.text(`Above Average (${above.length})`, 14, y);
      doc.setTextColor(0, 0, 0);
      doc.autoTable({
        startY: y + 4,
        head: [["Unit", "Incidents", "Diff"]],
        body: above.map(u => [u.unit, u.incidents, `+${(u.incidents - avg).toFixed(1)}`]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [22, 101, 52] },
        margin: { top: 12, bottom: 22 },
      });
      y = doc.lastAutoTable.finalY + 10;
    }
    if (below.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(239, 68, 68);
      doc.text(`Below Average (${below.length})`, 14, y);
      doc.setTextColor(0, 0, 0);
      doc.autoTable({
        startY: y + 4,
        head: [["Unit", "Incidents", "Diff"]],
        body: below.map(u => [u.unit, u.incidents, (u.incidents - avg).toFixed(1)]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [127, 29, 29] },
        margin: { top: 12, bottom: 22 },
      });
    }
    addPdfWatermarks(doc);
    doc.save(`unit_call_average_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const exportUnitOverviewCSV = () => {
    if (!stats?.unit_summary?.length) return;
    const headers = ["Unit", "Calls", "Dispatched", "Roll Calls", "Patrol Sessions", "Patrol Hours"];
    const rangeLabel = getDateRangeLabel();
    const lines = [];
    lines.push(`"CHSP IMS - Generated by: ${generatedBy} | ${format(new Date(), "MM/dd/yyyy h:mm a")}"`);
    if (rangeLabel) lines.push(`"${rangeLabel}"`);
    lines.push("");
    lines.push(headers.join(","));
    stats.unit_summary.forEach(u => {
      lines.push([u.unit, u.incidents, u.dispatched, u.rollcalls, u.patrol_sessions, u.patrol_hours].join(","));
    });
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `unit_overview_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportOptions(false);
  };

  const exportUnitOverviewPDF = async () => {
    if (!stats?.unit_summary?.length) return;
    const { default: jsPDF } = await import("jspdf");
    await import("jspdf-autotable");
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Unit Overview", 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${format(new Date(), "MM/dd/yyyy h:mm a")}  |  By: ${generatedBy}`, 14, 28);
    let startY = 34;
    const rangeLabel = getDateRangeLabel();
    if (rangeLabel) {
      doc.setTextColor(200, 120, 0);
      doc.text(rangeLabel, 14, startY);
      doc.setTextColor(0, 0, 0);
      startY += 8;
    }
    doc.autoTable({
      startY,
      head: [["Unit", "Calls", "Dispatched", "Roll Calls", "Patrol Sessions", "Patrol Hours"]],
      body: stats.unit_summary.map(u => [u.unit, u.incidents, u.dispatched, u.rollcalls, u.patrol_sessions, u.patrol_hours]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 41, 59] },
      margin: { top: 12, bottom: 22 },
    });
    addPdfWatermarks(doc);
    doc.save(`unit_overview_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    setShowExportOptions(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight">Command Dashboard</h1>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
          <Radio className="w-4 h-4 text-blue-500 animate-pulse" />
          <span className="font-mono">{currentTime.toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="dashboard-widget">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-3">
            <CalendarDays className="w-4 h-4 text-slate-500" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Filter by Date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
            />
            <span className="text-slate-500 text-sm">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
            />
            <Button
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => { setLoading(true); fetchDashboardData(dateFrom, dateTo); }}
              disabled={!dateFrom && !dateTo}
            >
              Apply
            </Button>
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-3 text-xs text-slate-400"
                onClick={() => { setDateFrom(""); setDateTo(""); setLoading(true); fetchDashboardData("", ""); }}
              >
                Clear
              </Button>
            )}
            {(dateFrom || dateTo) && (
              <span className="text-xs text-amber-400">
                Showing: {dateFrom || "start"} — {dateTo || "now"}
              </span>
            )}
            {/* Stats freshness + manual refresh (stats are cached server-side) */}
            <div className="flex items-center gap-2 ml-auto">
              {computedAt && (
                <span className="text-xs text-slate-500">{getUpdatedAgoLabel()}</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-slate-400"
                onClick={() => fetchDashboardData(undefined, undefined, true)}
                disabled={refreshing}
                title="Recompute live (bypass cache)"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing…" : "Refresh"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call Counter */}
      {stats?.call_counter && (
        <Card className="dashboard-widget">
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
              {[
                { label: "Today", total: stats.call_counter.today, incidents: stats.call_counter.incidents_today, live: stats.call_counter.live_today },
                { label: "This Week", total: stats.call_counter.this_week, incidents: stats.call_counter.incidents_week, live: stats.call_counter.live_week },
                { label: "This Month", total: stats.call_counter.this_month, incidents: stats.call_counter.incidents_month, live: stats.call_counter.live_month },
                { label: "This Year", total: stats.call_counter.this_year, incidents: stats.call_counter.incidents_year, live: stats.call_counter.live_year },
              ].map((period) => (
                <div key={period.label} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-white">{period.incidents}</p>
                  <p className="text-xs sm:text-sm text-slate-400 uppercase font-semibold">{period.label}</p>
                  <div className="flex justify-center gap-2 mt-1 text-[10px] text-slate-500">
                    <span>{period.incidents} inc</span>
                    <span>·</span>
                    <span>{period.live} live</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Overview - 2x3 on mobile, 5 cols on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => navigate("/incidents")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-blue-400">{stats?.total_incidents || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Total Incidents</p>
              </div>
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => navigate("/live-calls")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-red-400">{stats?.live_calls_active || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Dispatch</p>
                {stats?.live_calls_total > 0 && (
                  <p className="text-[10px] text-slate-600">{stats.live_calls_total} total</p>
                )}
              </div>
              <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-red-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={handleArrestsClick}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-400">{stats?.perp_arrests || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Arrests</p>
              </div>
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => navigate("/incidents")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-amber-400">{stats?.active_incidents || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Active Cases</p>
              </div>
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="dashboard-widget cursor-pointer hover:bg-slate-800/50 transition-colors"
          onClick={() => navigate("/incidents")}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-slate-400">{stats?.closed_incidents || 0}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase">Closed</p>
              </div>
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-slate-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row - Stack on mobile, 2 cols on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Incidents by Type */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              Incidents by Type
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.incidents_by_type || []).length} types
                <select
                  value={typeSort}
                  onChange={(e) => setTypeSort(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-[10px] text-slate-400 cursor-pointer"
                >
                  <option value="count_desc">Most → Least</option>
                  <option value="count_asc">Least → Most</option>
                  <option value="name_asc">A → Z</option>
                  <option value="name_desc">Z → A</option>
                </select>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportIncidentsByType("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportIncidentsByType("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {(() => {
              const sorted = [...(stats?.incidents_by_type || [])].sort((a, b) => {
                if (typeSort === "count_desc") return b.count - a.count;
                if (typeSort === "count_asc") return a.count - b.count;
                if (typeSort === "name_asc") return a.type.localeCompare(b.type);
                return b.type.localeCompare(a.type);
              });
              return (
            <div style={{ height: Math.max(200, sorted.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sorted} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis dataKey="type" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={120} interval={0} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Unit Activity Graph */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              Unit Activity (All Time)
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.units_responded || []).length} units
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportUnitActivity("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportUnitActivity("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div style={{ height: Math.max(200, (stats?.units_responded || []).length * 28) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.units_responded || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis dataKey="unit" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={50} interval={0} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="count" fill="#10b981" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dispatchers Row - Stack on mobile, 3 cols on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Dispatchers */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              Dispatchers Activity
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.dispatchers || []).length} dispatchers
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportDispatchers("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportDispatchers("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div className="h-48 sm:h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats?.dispatchers || []}
                    dataKey="count"
                    nameKey="dispatcher"
                    cx="50%"
                    cy="45%"
                    outerRadius={55}
                  >
                    {(stats?.dispatchers || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', fontSize: '12px' }}
                    formatter={(value, name) => [`${value} call${value === 1 ? '' : 's'}`, name]}
                  />
                  <Legend
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    iconSize={8}
                    wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
                    formatter={(value) => {
                      const entry = (stats?.dispatchers || []).find(d => d.dispatcher === value);
                      return entry ? `${value} (${entry.count})` : value;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top 5 Active Units - Last 7 Days */}
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              Top 5 Active Units
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportTop5("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportTop5("pdf")}>PDF</Button>
              </span>
            </CardTitle>
            <p className="text-xs text-slate-500 font-mono">
              {stats?.top_5_period_start} - {stats?.top_5_period_end}
            </p>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <ScrollArea className="h-32 sm:h-40">
              {(stats?.top_5_active_units || []).map((unit, index) => (
                <div key={unit.unit} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-sm bg-blue-600/20 text-blue-400 text-xs flex items-center justify-center font-mono">
                      {index + 1}
                    </span>
                    <span className="font-mono text-xs sm:text-sm">{unit.unit}</span>
                  </div>
                  <span className="text-slate-400 font-mono text-xs sm:text-sm">{unit.count}</span>
                </div>
              ))}
              {(!stats?.top_5_active_units || stats.top_5_active_units.length === 0) && (
                <p className="text-slate-500 text-xs sm:text-sm text-center py-4">No activity in last 7 days</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Units Investigating */}
        <Card className="dashboard-widget md:col-span-2 lg:col-span-1">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-500" />
              Units Investigating
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.units_investigating || []).length} units
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportInvestigating("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportInvestigating("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <ScrollArea className="h-32 sm:h-48">
              {(stats?.units_investigating || []).map((unit) => (
                <div key={unit.unit} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-0">
                  <span className="font-mono text-xs sm:text-sm">{unit.unit}</span>
                  <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-sm text-xs font-mono">
                    {unit.count} cases
                  </span>
                </div>
              ))}
              {(!stats?.units_investigating || stats.units_investigating.length === 0) && (
                <p className="text-slate-500 text-xs sm:text-sm text-center py-4">No active investigations</p>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Units Mismatch - Roll Call vs Incidents */}
      {((stats?.rollcall_only_units?.length > 0) || (stats?.incidents_only_units?.length > 0)) && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Roll Call / Incident Mismatch
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.rollcall_only_units?.length || 0) + (stats?.incidents_only_units?.length || 0)} units
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportMismatch("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportMismatch("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stats?.rollcall_only_units?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">In Roll Call but no Incidents ({stats.rollcall_only_units.length})</p>
                  <div className="space-y-1">
                    {stats.rollcall_only_units.map((u) => (
                      <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-slate-700">
                        <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                        <span className="text-[10px] text-slate-500">{u.rollcalls} roll calls</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {stats?.incidents_only_units?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 uppercase mb-2">In Incidents but no Roll Call ({stats.incidents_only_units.length})</p>
                  <div className="space-y-1">
                    {stats.incidents_only_units.map((u) => (
                      <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-slate-700">
                        <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                        <span className="text-[10px] text-slate-500">{u.incidents} incidents</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inactive Units - 0 incidents and 0 roll calls */}
      {stats?.unit_summary?.length > 0 && (() => {
        const inactive = stats.unit_summary.filter(u => u.incidents === 0 && u.rollcalls === 0).sort((a, b) => a.unit.localeCompare(b.unit));
        if (inactive.length === 0) return null;
        return (
          <Card className="dashboard-widget">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Inactive Units
                <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                  {inactive.length} units · 0 incidents & 0 roll calls
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportInactive("csv")}><Download className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportInactive("pdf")}>PDF</Button>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="flex flex-wrap gap-1.5">
                {inactive.map((u) => (
                  <Badge key={u.unit} variant="outline" className="text-xs font-mono border-red-900/40 text-red-400">
                    {u.unit}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Roll Call > Incidents */}
      {stats?.unit_summary?.length > 0 && (() => {
        const moreRollcall = stats.unit_summary.filter(u => u.rollcalls > u.incidents);
        if (moreRollcall.length === 0) return null;
        return (
          <Card className="dashboard-widget">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-orange-500" />
                More Roll Calls Than Incidents
                <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                  {moreRollcall.length} units
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportMoreRollcall("csv")}><Download className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportMoreRollcall("pdf")}>PDF</Button>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-1">
                {moreRollcall.map((u) => (
                  <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-slate-700">
                    <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                    <div className="flex items-center gap-3 text-[10px] sm:text-xs">
                      <span className="text-violet-400">{u.rollcalls} roll calls</span>
                      <span className="text-slate-600">|</span>
                      <span className="text-blue-400">{u.incidents} incidents</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Inactive Units */}
      {stats?.inactive_units?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              No Activity ({stats.inactive_units.length} units)
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="flex flex-wrap gap-1.5">
              {stats.inactive_units.map((unit) => (
                <Badge key={unit} variant="outline" className="text-xs text-slate-500 border-slate-700 font-mono">
                  {unit}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Below / Above Average on Calls */}
      {stats?.unit_summary?.length > 0 && (() => {
        const presetLabels = { all: "All Time", ytd: "YTD", "6m": "6 Months", "1m": "1 Month", "1w": "1 Week" };
        const sourceData = avgPreset === "all"
          ? stats.unit_summary
          : (stats.unit_avg_presets?.[avgPreset] || []);
        const unitsWithCalls = sourceData.filter(u => u.incidents > 0);
        if (unitsWithCalls.length === 0 && avgPreset !== "all") {
          // Show card with empty state for presets
        }
        const totalCalls = unitsWithCalls.reduce((s, u) => s + u.incidents, 0);
        const avg = unitsWithCalls.length > 0 ? totalCalls / unitsWithCalls.length : 0;
        const allUnitsForPreset = avgPreset === "all"
          ? stats.unit_summary
          : [...sourceData, ...stats.unit_summary.filter(s => !sourceData.find(p => p.unit === s.unit)).map(s => ({ unit: s.unit, incidents: 0 }))];
        // "Near average" band — within max(1, 15% of avg) of the avg.
        // Picks up units that are essentially average instead of forcing
        // them into below/above on a hairline difference.
        const nearThreshold = Math.max(1, avg * 0.15);
        const below = allUnitsForPreset.filter(u => u.incidents < avg - nearThreshold).sort((a, b) => a.incidents - b.incidents);
        const near = allUnitsForPreset.filter(u => u.incidents > 0 && Math.abs(u.incidents - avg) <= nearThreshold).sort((a, b) => b.incidents - a.incidents);
        const above = allUnitsForPreset.filter(u => u.incidents > avg + nearThreshold).sort((a, b) => b.incidents - a.incidents);
        return (
          <Card className="dashboard-widget">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-blue-500" />
                Unit Call Average
                <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-2">
                  {presetLabels[avgPreset]} · {totalCalls} total calls ÷ {unitsWithCalls.length} active units = avg {avg.toFixed(1)}
                  <Button variant="outline" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => { setAvgUnitDialog(""); setAvgUnitSearch(""); }}>
                    Per Unit
                  </Button>
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={exportAvgCSV}><Download className="w-3 h-3 mr-1" />CSV</Button>
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={exportAvgPDF}><Download className="w-3 h-3 mr-1" />PDF</Button>
                </span>
              </CardTitle>
              <div className="flex gap-1 mt-2">
                {Object.entries(presetLabels).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setAvgPreset(key)}
                    className={`px-2 py-1 text-[10px] rounded font-medium transition-colors ${avgPreset === key ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {unitsWithCalls.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No unit responses in this time frame.</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {below.length > 0 && (
                    <div>
                      <p className="text-xs text-red-400 font-semibold mb-2 uppercase tracking-wider">Below Average ({below.length})</p>
                      <div className="space-y-1">
                        {below.map((u) => (
                          <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-red-900/30">
                            <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                            <span className="text-xs text-red-400">{u.incidents} incidents</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {near.length > 0 && (
                    <div>
                      <p className="text-xs text-amber-400 font-semibold mb-2 uppercase tracking-wider">Near Average ({near.length})</p>
                      <div className="space-y-1">
                        {near.map((u) => (
                          <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-amber-900/30">
                            <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                            <span className="text-xs text-amber-400">{u.incidents} incidents</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {above.length > 0 && (
                    <div>
                      <p className="text-xs text-green-400 font-semibold mb-2 uppercase tracking-wider">Above Average ({above.length})</p>
                      <div className="space-y-1">
                        {above.map((u) => (
                          <div key={u.unit} className="flex items-center justify-between py-1.5 px-2 bg-slate-800/50 rounded border border-green-900/30">
                            <span className="font-mono text-xs sm:text-sm">{u.unit}</span>
                            <span className="text-xs text-green-400">{u.incidents} incidents</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Per-Unit Average drill-down dialog. Picks a unit, shows incidents
          vs the all-units average for each preset (1w, 1m, 6m, YTD, all). */}
      {stats?.unit_summary && (
        <Dialog open={avgUnitDialog !== null} onOpenChange={(open) => { if (!open) setAvgUnitDialog(null); }}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Unit Call Average — Per Unit</DialogTitle>
            </DialogHeader>
            {(() => {
              const presetLabels = { "1w": "1 Week", "1m": "1 Month", "6m": "6 Months", ytd: "YTD", all: "All Time" };
              const allUnits = [...new Set([
                ...(stats.unit_summary || []).map(u => u.unit),
                ...Object.values(stats.unit_avg_presets || {}).flatMap(arr => (arr || []).map(u => u.unit)),
              ])].sort((a, b) => String(a).localeCompare(String(b)));
              const filteredUnits = allUnits.filter(u => {
                const q = avgUnitSearch.trim().toLowerCase();
                return !q || String(u).toLowerCase().includes(q);
              });
              const dataFor = (preset) =>
                preset === "all" ? (stats.unit_summary || []) : (stats.unit_avg_presets?.[preset] || []);
              const avgFor = (preset) => {
                const d = dataFor(preset).filter(u => u.incidents > 0);
                return d.length > 0 ? d.reduce((s, u) => s + u.incidents, 0) / d.length : 0;
              };
              const incidentsFor = (preset, unit) => {
                const found = dataFor(preset).find(u => u.unit === unit);
                return found?.incidents || 0;
              };

              if (!avgUnitDialog) {
                // Picker view
                return (
                  <div className="space-y-3">
                    <input
                      type="text"
                      autoFocus
                      value={avgUnitSearch}
                      onChange={(e) => setAvgUnitSearch(e.target.value)}
                      placeholder="Search a unit…"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm focus:outline-none focus:border-blue-500"
                    />
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-[60vh] overflow-y-auto">
                      {filteredUnits.length === 0 ? (
                        <p className="col-span-full text-xs text-slate-500 text-center py-4">No units found.</p>
                      ) : filteredUnits.map(u => (
                        <Button
                          key={u}
                          variant="outline"
                          size="sm"
                          className="h-8 font-mono text-xs"
                          onClick={() => setAvgUnitDialog(u)}
                        >
                          {u}
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              }

              // Detail view for a chosen unit
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAvgUnitDialog("")}>
                      ← Back to units
                    </Button>
                    <span className="font-mono text-base font-bold">{avgUnitDialog}</span>
                  </div>
                  <div className="space-y-1.5">
                    {Object.entries(presetLabels).map(([key, label]) => {
                      const inc = incidentsFor(key, avgUnitDialog);
                      const a = avgFor(key);
                      const threshold = Math.max(1, a * 0.15);
                      const diff = inc - a;
                      let band = "near";
                      let bandColor = "text-amber-400";
                      let bandLabel = "near avg";
                      if (inc < a - threshold) { band = "below"; bandColor = "text-red-400"; bandLabel = "below avg"; }
                      else if (inc > a + threshold) { band = "above"; bandColor = "text-green-400"; bandLabel = "above avg"; }
                      const sign = diff >= 0 ? "+" : "";
                      return (
                        <div key={key} className="flex items-center justify-between p-2 bg-slate-800/50 rounded border border-slate-700">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
                            <p className={`text-[10px] ${bandColor}`}>{bandLabel}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">
                              <span className="font-bold">{inc}</span>
                              <span className="text-slate-500"> / avg {a.toFixed(1)}</span>
                            </p>
                            <p className={`text-[11px] font-mono ${bandColor}`}>
                              {sign}{diff.toFixed(1)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Response Analytics */}
      {stats?.response_analytics && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-500" />
              Response Analytics
              {stats.response_analytics.start_date && (
                <span className="text-[10px] text-emerald-500 font-normal">
                  Since {new Date(stats.response_analytics.start_date).toLocaleDateString("en-US", { timeZone: "America/New_York" })}
                </span>
              )}
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {!stats.response_analytics.start_date && (
                  <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-red-400 hover:text-red-300" onClick={async () => {
                    if (!window.confirm("Reset response analytics? This will start tracking from now.")) return;
                    try {
                      await axios.post(`${API}/dashboard/response-analytics-reset`, {}, { withCredentials: true });
                      fetchDashboardData();
                    } catch (e) { console.error(e); }
                  }}>Reset</Button>
                )}
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportResponseAnalytics("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportResponseAnalytics("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {(() => {
              const ra = stats.response_analytics;
              const sinceLabel = ra.start_date ? `Since ${new Date(ra.start_date).toLocaleDateString("en-US", { timeZone: "America/New_York" })}` : "All time";
              return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-cyan-400">{ra.avg_to_first_assigned}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg to First Unit Assigned</p>
                <p className="text-[9px] text-slate-600">{ra.sample_assigned} calls tracked</p>
                <p className="text-[8px] text-slate-700 mt-0.5">{sinceLabel}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-emerald-400">{ra.avg_to_first_84}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg to First Unit 84</p>
                <p className="text-[9px] text-slate-600">{ra.sample_84} calls tracked</p>
                <p className="text-[8px] text-slate-700 mt-0.5">{sinceLabel}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-amber-400">{ra.avg_to_first_98}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg to First Unit 98</p>
                <p className="text-[9px] text-slate-600">{ra.sample_98} calls tracked</p>
                <p className="text-[8px] text-slate-700 mt-0.5">{sinceLabel}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-blue-400">{ra.avg_to_call_98}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg to Call 98</p>
                <p className="text-[9px] text-slate-600">{ra.sample_call_98} calls tracked</p>
                <p className="text-[8px] text-slate-700 mt-0.5">{sinceLabel}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-violet-400">{ra.avg_units_per_call}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg Units Per Call</p>
                <p className="text-[9px] text-slate-600">{ra.sample_units} calls</p>
                <p className="text-[8px] text-slate-700 mt-0.5">All time</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-orange-400">{ra.hotline_pct}%</p>
                <p className="text-[10px] text-slate-400 mt-1">Hotline Calls (All)</p>
                <p className="text-[9px] text-slate-600">{ra.hotline_count} of {ra.total_incidents}</p>
                <p className="text-[8px] text-slate-700 mt-0.5">All time</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-orange-900/30 text-center">
                <p className="text-lg sm:text-xl font-bold text-orange-300">{ra.hotline_live_pct}%</p>
                <p className="text-[10px] text-slate-400 mt-1">Hotline Dispatch</p>
                <p className="text-[9px] text-slate-600">{ra.hotline_live_count} reported live / from live call</p>
                <p className="text-[8px] text-slate-700 mt-0.5">All time</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded border border-slate-700 text-center">
                <p className="text-lg sm:text-xl font-bold text-pink-400">{ra.avg_hotline_to_dispatch}</p>
                <p className="text-[10px] text-slate-400 mt-1">Avg Hotline to Dispatch</p>
                <p className="text-[9px] text-slate-600">{ra.sample_hotline_dispatch} calls matched</p>
                <p className="text-[8px] text-slate-700 mt-0.5">All time</p>
              </div>
            </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Average Calls Per Unit by Period */}
      {stats?.call_counter && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-500" />
              Avg Responses Per Unit by Period
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                total unit-responses ÷ active units per period
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportAvgByPeriod("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportAvgByPeriod("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="grid grid-cols-4 gap-3 sm:gap-6">
              {[
                { label: "Today", avg: stats.call_counter.avg_today, responses: stats.call_counter.responses_today, units: stats.call_counter.active_units_today },
                { label: "This Week", avg: stats.call_counter.avg_week, responses: stats.call_counter.responses_week, units: stats.call_counter.active_units_week },
                { label: "This Month", avg: stats.call_counter.avg_month, responses: stats.call_counter.responses_month, units: stats.call_counter.active_units_month },
                { label: "This Year", avg: stats.call_counter.avg_year, responses: stats.call_counter.responses_year, units: stats.call_counter.active_units_year },
              ].map((p) => (
                <div key={p.label} className="text-center">
                  <p className="text-2xl sm:text-3xl font-bold text-cyan-400">{p.avg}</p>
                  <p className="text-xs sm:text-sm text-slate-400 uppercase font-semibold">{p.label}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{p.responses} responses · {p.units} units</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Location Hotspots */}
      {stats?.location_hotspots?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              Incident Locations
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {stats.location_hotspots.length} locations
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportLocations("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportLocations("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <ScrollArea className="h-[400px]">
              <div className="space-y-1">
                {stats.location_hotspots.map((loc, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded border border-slate-700">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 w-5 flex-shrink-0">#{idx + 1}</span>
                        <span className="text-sm truncate">{loc.location}</span>
                      </div>
                      {loc.types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 ml-7">
                          {loc.types.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] py-0 px-1">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <span className="text-sm font-bold text-red-400">{loc.count}</span>
                      <span className="text-[10px] text-slate-500">calls</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Repeat Locations */}
      {stats?.repeat_locations?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-orange-500" />
              Repeat Locations
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {stats.repeat_locations.length} locations
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportRepeatLocations("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportRepeatLocations("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <ScrollArea className="h-[400px]">
              <div className="space-y-1">
                {stats.repeat_locations.map((loc, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded border border-orange-900/30 cursor-pointer hover:bg-slate-700/50 transition-colors"
                    onClick={() => {
                      setLocationDialogTitle(loc.location + (loc.cross_streets ? ` (near ${loc.cross_streets})` : ""));
                      setLocationIncidents(loc.incidents || []);
                      setShowLocationDialog(true);
                    }}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 w-5 flex-shrink-0">#{idx + 1}</span>
                        <span className="text-sm truncate">{loc.location}</span>
                      </div>
                      <div className="ml-7 mt-0.5 text-[10px] text-slate-500">
                        Cross: {loc.cross_streets}
                      </div>
                      {loc.types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 ml-7">
                          {loc.types.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] py-0 px-1">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <span className="text-sm font-bold text-orange-400">{loc.count}</span>
                      <span className="text-[10px] text-slate-500">calls</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Intersection Hotspots */}
      {stats?.intersection_hotspots?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              Repeat Intersections
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {stats.intersection_hotspots.length} intersections
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportIntersections("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportIntersections("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <ScrollArea className="h-[400px]">
              <div className="space-y-1">
                {stats.intersection_hotspots.map((loc, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-800/50 rounded border border-red-900/30 cursor-pointer hover:bg-slate-700/50 transition-colors"
                    onClick={() => {
                      setLocationDialogTitle(loc.intersection);
                      setLocationIncidents(loc.incidents || []);
                      setShowLocationDialog(true);
                    }}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-500 w-5 flex-shrink-0">#{idx + 1}</span>
                        <span className="text-sm truncate">{loc.intersection}</span>
                      </div>
                      {loc.types?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1 ml-7">
                          {loc.types.map((t, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] py-0 px-1">{t}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <span className="text-sm font-bold text-red-400">{loc.count}</span>
                      <span className="text-[10px] text-slate-500">calls</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Patrol Activity Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {/* Patrol Sessions by Unit */}
        {stats?.unit_summary?.length > 0 && (() => {
          const patrolUnits = stats.unit_summary.filter(u => u.patrol_sessions > 0).sort((a, b) => b.patrol_sessions - a.patrol_sessions);
          if (patrolUnits.length === 0) return null;
          return (
            <Card className="dashboard-widget">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Car className="w-4 h-4 text-emerald-500" />
                  Patrol Sessions by Unit
                  <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                    {patrolUnits.length} units
                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportPatrolSessions("csv")}><Download className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportPatrolSessions("pdf")}>PDF</Button>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div style={{ height: Math.max(200, patrolUnits.length * 28) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={patrolUnits.map(u => ({ unit: u.unit, count: u.patrol_sessions }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
                      <YAxis dataKey="unit" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={50} interval={0} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '2px', fontSize: '12px' }}
                      />
                      <Bar dataKey="count" fill="#10b981" radius={[0, 2, 2, 0]} name="Sessions" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Patrol Hours by Unit */}
        {stats?.unit_summary?.length > 0 && (() => {
          const patrolUnits = stats.unit_summary.filter(u => u.patrol_hours > 0).sort((a, b) => b.patrol_hours - a.patrol_hours);
          if (patrolUnits.length === 0) return null;
          return (
            <Card className="dashboard-widget">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Patrol Hours by Unit
                  <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                    {patrolUnits.length} units
                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportPatrolHours("csv")}><Download className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportPatrolHours("pdf")}>PDF</Button>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <div style={{ height: Math.max(200, patrolUnits.length * 28) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={patrolUnits.map(u => ({ unit: u.unit, hours: u.patrol_hours }))} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                      <YAxis dataKey="unit" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={50} interval={0} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '2px', fontSize: '12px' }}
                        formatter={(value) => [`${value} hrs`, 'Hours']}
                      />
                      <Bar dataKey="hours" fill="#f59e0b" radius={[0, 2, 2, 0]} name="Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* Unit Overview */}
      {stats?.unit_summary?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-cyan-500" />
              Unit Overview
              <span className="text-[10px] text-slate-600 font-normal ml-auto mr-2">
                {stats.unit_summary.length} units
              </span>
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                  onClick={() => setShowExportOptions(!showExportOptions)}
                >
                  <Download className="w-3.5 h-3.5" />
                </Button>
                {showExportOptions && (
                  <div className="absolute right-0 top-7 z-10 bg-slate-800 border border-slate-700 rounded shadow-lg py-1 min-w-[100px]">
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-white"
                      onClick={exportUnitOverviewCSV}
                    >
                      Export CSV
                    </button>
                    <button
                      className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700 hover:text-white"
                      onClick={exportUnitOverviewPDF}
                    >
                      Export PDF
                    </button>
                  </div>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {/* Sort controls - mobile */}
            <div className="flex items-center gap-1 mb-2 flex-wrap sm:hidden">
              <span className="text-[10px] text-slate-500 mr-1">Sort:</span>
              {[
                { key: "unit", label: "Unit" },
                { key: "incidents", label: "Calls" },
                { key: "dispatched", label: "Disp" },
                { key: "rollcalls", label: "RC" },
                { key: "patrol_sessions", label: "Patrol" },
                { key: "patrol_hours", label: "Hrs" },
              ].map((col) => (
                <button
                  key={col.key}
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${unitSort.key === col.key ? "border-cyan-600 text-cyan-400 bg-cyan-900/20" : "border-slate-700 text-slate-500"}`}
                  onClick={() => handleUnitSort(col.key)}
                >
                  {col.label}{unitSort.key === col.key ? (unitSort.dir === "desc" ? "↓" : "↑") : ""}
                </button>
              ))}
            </div>

            {/* Mobile card layout */}
            <div className="sm:hidden space-y-2">
              {getSortedUnitSummary().map((u) => (
                <div
                  key={u.unit}
                  className="p-2.5 bg-slate-800/50 rounded border border-slate-700 active:border-cyan-600 transition-colors"
                  onClick={() => fetchUnitDetail(u.unit, "incidents")}
                >
                  <p className="font-mono font-medium text-sm text-cyan-400 mb-1.5">{u.unit}</p>
                  <div className="grid grid-cols-5 gap-1 text-center">
                    <div onClick={(e) => { e.stopPropagation(); if (u.incidents > 0) fetchUnitDetail(u.unit, "incidents"); }}>
                      <p className={`text-sm font-bold ${u.incidents > 0 ? "text-blue-400" : "text-slate-600"}`}>{u.incidents}</p>
                      <p className="text-[9px] text-slate-500">Calls</p>
                    </div>
                    <div onClick={(e) => { e.stopPropagation(); if (u.dispatched > 0) fetchUnitDetail(u.unit, "dispatched"); }}>
                      <p className={`text-sm font-bold ${u.dispatched > 0 ? "text-purple-400" : "text-slate-600"}`}>{u.dispatched}</p>
                      <p className="text-[9px] text-slate-500">Disp</p>
                    </div>
                    <div onClick={(e) => { e.stopPropagation(); if (u.rollcalls > 0) fetchUnitDetail(u.unit, "rollcalls"); }}>
                      <p className={`text-sm font-bold ${u.rollcalls > 0 ? "text-violet-400" : "text-slate-600"}`}>{u.rollcalls}</p>
                      <p className="text-[9px] text-slate-500">RC</p>
                    </div>
                    <div onClick={(e) => { e.stopPropagation(); if (u.patrol_sessions > 0) fetchUnitDetail(u.unit, "patrols"); }}>
                      <p className={`text-sm font-bold ${u.patrol_sessions > 0 ? "text-emerald-400" : "text-slate-600"}`}>{u.patrol_sessions}</p>
                      <p className="text-[9px] text-slate-500">Patrol</p>
                    </div>
                    <div onClick={(e) => { e.stopPropagation(); if (u.patrol_hours > 0) fetchUnitDetail(u.unit, "patrols"); }}>
                      <p className={`text-sm font-bold ${u.patrol_hours > 0 ? "text-amber-400" : "text-slate-600"}`}>{u.patrol_hours}</p>
                      <p className="text-[9px] text-slate-500">Hrs</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-500 uppercase text-[10px] sm:text-xs">
                    {[
                      { key: "unit", label: "Unit", align: "text-left" },
                      { key: "incidents", label: "Calls", align: "text-center" },
                      { key: "dispatched", label: "Dispatched", align: "text-center" },
                      { key: "rollcalls", label: "Roll Calls", align: "text-center" },
                      { key: "patrol_sessions", label: "Patrols", align: "text-center" },
                      { key: "patrol_hours", label: "Hours", align: "text-center" },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className={`${col.align} py-2 px-1 cursor-pointer hover:text-slate-300 transition-colors select-none`}
                        onClick={() => handleUnitSort(col.key)}
                      >
                        <span className="inline-flex items-center gap-0.5">
                          {col.label}
                          {unitSort.key === col.key && (
                            <span className="text-cyan-400">{unitSort.dir === "desc" ? "↓" : "↑"}</span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getSortedUnitSummary().map((u) => (
                    <tr key={u.unit} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td
                        className="py-1.5 pr-2 font-mono font-medium cursor-pointer hover:text-cyan-400 transition-colors"
                        onClick={() => fetchUnitDetail(u.unit, "incidents")}
                      >{u.unit}</td>
                      <td className="text-center py-1.5 px-1">
                        <span
                          className={`${u.incidents > 0 ? "text-blue-400 cursor-pointer hover:underline" : "text-slate-600"}`}
                          onClick={u.incidents > 0 ? () => fetchUnitDetail(u.unit, "incidents") : undefined}
                        >{u.incidents}</span>
                      </td>
                      <td className="text-center py-1.5 px-1">
                        <span
                          className={`${u.dispatched > 0 ? "text-purple-400 cursor-pointer hover:underline" : "text-slate-600"}`}
                          onClick={u.dispatched > 0 ? () => fetchUnitDetail(u.unit, "dispatched") : undefined}
                        >{u.dispatched}</span>
                      </td>
                      <td className="text-center py-1.5 px-1">
                        <span
                          className={`${u.rollcalls > 0 ? "text-violet-400 cursor-pointer hover:underline" : "text-slate-600"}`}
                          onClick={u.rollcalls > 0 ? () => fetchUnitDetail(u.unit, "rollcalls") : undefined}
                        >{u.rollcalls}</span>
                      </td>
                      <td className="text-center py-1.5 px-1">
                        <span
                          className={`${u.patrol_sessions > 0 ? "text-emerald-400 cursor-pointer hover:underline" : "text-slate-600"}`}
                          onClick={u.patrol_sessions > 0 ? () => fetchUnitDetail(u.unit, "patrols") : undefined}
                        >{u.patrol_sessions}</span>
                      </td>
                      <td className="text-center py-1.5 px-1">
                        <span
                          className={`${u.patrol_hours > 0 ? "text-amber-400 cursor-pointer hover:underline" : "text-slate-600"}`}
                          onClick={u.patrol_hours > 0 ? () => fetchUnitDetail(u.unit, "patrols") : undefined}
                        >{u.patrol_hours}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Roll Call Attendance */}
      {stats?.rollcall_units?.length > 0 && (
        <Card className="dashboard-widget">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle className="text-xs sm:text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-violet-500" />
              Roll Call Attendance
              <span className="text-[10px] text-slate-600 font-normal ml-auto flex items-center gap-1">
                {(stats?.rollcall_units || []).length} units | {stats?.rollcall_total || 0} roll calls
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportRollCall("csv")}><Download className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => exportRollCall("pdf")}>PDF</Button>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            <div style={{ height: Math.max(200, (stats?.rollcall_units || []).length * 28) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.rollcall_units || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
                  <YAxis dataKey="unit" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={50} interval={0} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 2, 2, 0]} name="Roll Calls" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Unit Detail Dialog */}
      <Dialog open={showUnitDialog} onOpenChange={setShowUnitDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <Users className="w-5 h-5" />
              {unitDetail?.unit || "Unit"} Detail
            </DialogTitle>
          </DialogHeader>

          {/* Tab buttons */}
          <div className="flex flex-wrap gap-1">
            {[
              { key: "incidents", label: "Calls", count: unitDetail?.totals?.incidents },
              { key: "dispatched", label: "Dispatched", count: unitDetail?.totals?.dispatched },
              { key: "rollcalls", label: "Roll Calls", count: unitDetail?.totals?.rollcalls },
              { key: "patrols", label: "Patrols", count: unitDetail?.totals?.patrols },
            ].map((t) => (
              <Button
                key={t.key}
                variant={unitTab === t.key ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setUnitTab(t.key)}
              >
                {t.label} ({t.count || 0})
              </Button>
            ))}
          </div>

          <ScrollArea className="h-[55vh] pr-4">
            {loadingUnit ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
              </div>
            ) : !unitDetail ? (
              <p className="text-slate-500 text-center py-8">No data</p>
            ) : unitTab === "incidents" ? (
              unitDetail.incidents.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No incidents</p>
              ) : (
                <div className="space-y-2">
                  {unitDetail.incidents.map((inc) => (
                    <div
                      key={inc.incident_id}
                      className="p-3 bg-slate-800 rounded border border-slate-700 cursor-pointer hover:bg-slate-700 hover:border-blue-600/50 transition-colors"
                      onClick={() => { setShowUnitDialog(false); navigate(`/incidents/${inc.incident_id}`); }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                        <p className="text-sm text-slate-300 min-w-0 break-words sm:flex-1">{inc.headline || "No headline"}</p>
                        <div className="flex flex-wrap items-center gap-1 sm:shrink-0">
                          {inc.from_live_call && (
                            <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px] px-1.5 py-0">LIVE CALL</Badge>
                          )}
                          {inc.incident_type && (
                            <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/50 text-[10px] px-1.5 py-0">{inc.incident_type}</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">#{inc.incident_id} | {formatUnitDate(inc.date_time)}</p>
                    </div>
                  ))}
                </div>
              )
            ) : unitTab === "dispatched" ? (
              unitDetail.dispatched.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No dispatched incidents</p>
              ) : (
                <div className="space-y-2">
                  {unitDetail.dispatched.map((inc) => (
                    <div
                      key={inc.incident_id}
                      className="p-3 bg-slate-800 rounded border border-slate-700 cursor-pointer hover:bg-slate-700 hover:border-purple-600/50 transition-colors"
                      onClick={() => { setShowUnitDialog(false); navigate(`/incidents/${inc.incident_id}`); }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                        <p className="text-sm text-slate-300 min-w-0 break-words sm:flex-1">{inc.headline || "No headline"}</p>
                        <div className="flex flex-wrap items-center gap-1 sm:shrink-0">
                          {inc.from_live_call && (
                            <Badge className="bg-red-600/20 text-red-400 border-red-600/50 text-[10px] px-1.5 py-0">LIVE CALL</Badge>
                          )}
                          {inc.incident_type && (
                            <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 text-[10px] px-1.5 py-0">{inc.incident_type}</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">#{inc.incident_id} | {formatUnitDate(inc.date_time)}</p>
                    </div>
                  ))}
                </div>
              )
            ) : unitTab === "rollcalls" ? (
              unitDetail.rollcalls.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No roll calls</p>
              ) : (
                <div className="space-y-2">
                  {unitDetail.rollcalls.map((rc) => (
                    <div
                      key={rc.rollcall_id}
                      className="p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="w-3.5 h-3.5 text-violet-400" />
                          <span className="text-sm text-slate-300">{formatUnitDate(rc.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {rc.parsha && (
                            <Badge className="bg-amber-900/50 text-amber-300 border-amber-700 text-[10px] px-1.5 py-0">{rc.parsha}</Badge>
                          )}
                          <span className="text-[10px] text-slate-500">{rc.unit_count} units</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : unitTab === "patrols" ? (
              unitDetail.patrols.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No patrol sessions</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-2 py-1 text-[10px] text-slate-500 uppercase">
                    <span>Total: {unitDetail.totals.patrols} sessions</span>
                    <span>{unitDetail.totals.patrol_hours} hours</span>
                  </div>
                  {unitDetail.patrols.map((p, idx) => (
                    <div
                      key={p.log_id || idx}
                      className="p-3 bg-slate-800 rounded border border-slate-700 hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Car className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-sm text-slate-300 font-mono">{p.car || "N/A"}</span>
                        </div>
                        <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px] px-1.5 py-0">
                          {p.hours} hrs
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-1.5 text-xs text-slate-500">
                        <span>Start: {formatUnitDateTime(p.start)}</span>
                        <span>End: {formatUnitDateTime(p.end)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Arrests Dialog */}
      <Dialog open={showArrestsDialog} onOpenChange={setShowArrestsDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Arrest Records ({arrestRecords.length})
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            {loadingArrests ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
              </div>
            ) : arrestRecords.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No arrest records found</p>
            ) : (
              <div className="space-y-2">
                {arrestRecords.map((arrest, idx) => (
                  <div
                    key={`${arrest.incident_id}-${arrest.perp_id || idx}`}
                    className="p-3 bg-slate-800 rounded border border-slate-700 cursor-pointer hover:bg-slate-700 hover:border-emerald-600/50 transition-colors"
                    onClick={() => {
                      setShowArrestsDialog(false);
                      navigate(`/incidents/${arrest.incident_id}`);
                    }}
                  >
                    {/* Date and type - top row on mobile */}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">{arrest.incident_type}</Badge>
                      <span className="text-xs text-slate-400">{formatDateTime(arrest.date_time)}</span>
                    </div>
                    {/* Perp badges */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className="bg-emerald-100 dark:bg-emerald-600/20 text-emerald-700 dark:text-emerald-400 border-emerald-600/50">
                        {arrest.perp_description}
                      </Badge>
                      {arrest.linked_profile_name && (
                        <Badge variant="outline" className="text-blue-400 border-blue-600/50">
                          {arrest.linked_profile_name}
                        </Badge>
                      )}
                    </div>
                    {/* Headline and address */}
                    <p className="text-sm text-slate-300 mt-1">{arrest.headline}</p>
                    <p className="text-xs text-slate-500 mt-1">{arrest.address}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
      {/* Location Incidents Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={(open) => { setShowLocationDialog(open); if (!open) setConfirmNavIncident(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {locationDialogTitle}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {locationIncidents.map((inc, idx) => (
                <div key={idx} className="p-3 bg-slate-800/50 rounded border border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors"
                  onClick={() => setConfirmNavIncident(inc)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-mono text-slate-500">#{idx + 1}</span>
                      {inc.call_number && <Badge variant="outline" className="text-[10px] py-0 px-1">#{inc.call_number}</Badge>}
                      <span className="text-sm truncate">{inc.address || "No address"}</span>
                    </div>
                    <Badge className={`text-[10px] py-0 px-1 flex-shrink-0 ${inc.status === "closed" ? "bg-slate-600" : inc.status === "arrest" ? "bg-red-600" : "bg-blue-600"}`}>
                      {inc.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                    {inc.type && <span>{inc.type}</span>}
                    {inc.date && <span>{inc.date}</span>}
                  </div>
                </div>
              ))}
              {locationIncidents.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No linked incidents found</p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Confirm Navigation Dialog */}
      <Dialog open={!!confirmNavIncident} onOpenChange={(open) => { if (!open) setConfirmNavIncident(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Go to Incident?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {confirmNavIncident && (
              <div className="text-sm text-slate-300">
                <p>Navigate to incident {confirmNavIncident.call_number ? `#${confirmNavIncident.call_number}` : ""} at <span className="text-white font-medium">{confirmNavIncident.address || "unknown location"}</span>?</p>
                <p className="text-[10px] text-slate-500 mt-1">{confirmNavIncident.type} · {confirmNavIncident.date}</p>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setConfirmNavIncident(null)}>Cancel</Button>
              <Button size="sm" onClick={() => {
                const id = confirmNavIncident?.id;
                setConfirmNavIncident(null);
                setShowLocationDialog(false);
                if (id) navigate(`/incidents/${id}`);
              }}>Go to Incident</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
